import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { ArrowUpRight } from "lucide-react";
import { api, resolveAssetUrl } from "@/lib/api";

// Asymmetrical masonry — works for any number of projects, cycles a 8-cell pattern.
const layoutClasses = [
    "lg:col-span-5 lg:row-span-2 aspect-[4/5]",
    "lg:col-span-7 aspect-[16/10]",
    "lg:col-span-4 aspect-[4/5]",
    "lg:col-span-3 aspect-[4/5]",
    "lg:col-span-7 aspect-[16/9]",
    "lg:col-span-5 aspect-[4/5]",
    "lg:col-span-6 aspect-[16/10]",
    "lg:col-span-6 aspect-[16/10]",
];

export default function PortfolioSection() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/portfolio");
                setProjects(data);
            } catch {
                setProjects([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <section
            id="portfolio"
            data-testid="section-portfolio"
            className="relative bg-ink-800 py-28 lg:py-36"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <Reveal>
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20">
                        <div>
                            <p className="text-xs uppercase tracking-widest text-brass">
                                Portfolio — 03
                            </p>
                            <h2
                                data-testid="portfolio-title"
                                className="mt-6 font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-2xl"
                            >
                                Progetti
                                <span className="italic text-brass">
                                    {" "}
                                    recenti
                                </span>
                                .
                            </h2>
                        </div>
                        <p className="text-base text-cream/65 leading-relaxed max-w-md">
                            Una selezione di interventi residenziali e
                            commerciali. Clicca un progetto per vederne tutte
                            le immagini e i dettagli tecnici.
                        </p>
                    </div>
                </Reveal>

                {loading ? (
                    <p className="text-cream/40 text-sm" data-testid="portfolio-loading">
                        Caricamento progetti…
                    </p>
                ) : projects.length === 0 ? (
                    <p className="text-cream/40 text-sm">
                        Nessun progetto pubblicato.
                    </p>
                ) : (
                    <div
                        data-testid="portfolio-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-6"
                    >
                        {projects.map((p, i) => (
                            <Reveal
                                key={p.slug}
                                delay={Math.min(i * 80, 320)}
                                className={
                                    layoutClasses[i % layoutClasses.length]
                                }
                            >
                                <Link
                                    to={`/portfolio/${p.slug}`}
                                    data-testid={`portfolio-item-${p.slug}`}
                                    className="group relative block w-full h-full overflow-hidden bg-ink-700"
                                >
                                    <img
                                        src={resolveAssetUrl(p.cover)}
                                        alt={p.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-all duration-1000 group-hover:scale-105 grayscale-[20%] group-hover:grayscale-0"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/95 via-ink-900/30 to-transparent" />
                                    <div className="relative h-full w-full flex flex-col justify-end p-6 lg:p-8 z-10">
                                        <p className="text-[10px] uppercase tracking-widest text-brass">
                                            {p.category}
                                            {p.location ? ` — ${p.location}` : ""}
                                        </p>
                                        <div className="flex items-end justify-between gap-4 mt-3">
                                            <h3 className="font-serif text-2xl lg:text-3xl text-cream font-light leading-tight">
                                                {p.title}
                                            </h3>
                                            <span className="shrink-0 w-10 h-10 rounded-full border border-cream/30 flex items-center justify-center text-cream group-hover:bg-brass group-hover:border-brass group-hover:text-ink-900 transition-all duration-700">
                                                <ArrowUpRight size={16} />
                                            </span>
                                        </div>
                                        <p className="mt-3 text-sm text-cream/70 max-w-md opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                                            {p.excerpt}
                                        </p>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
